<script setup lang="ts">
import type { Collections } from '@nuxt/content'
import { i18n } from '~/config/i18n'

const socials = useSocials()
const { t, locale } = useI18n()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const LEGAL_PAGES_PATH_PREFIX = '/legal'

const { data: navigation } = await useAsyncData('navigation', async () => {
  const collection = `content_${locale.value}` as keyof Collections
  const fallbackCollection = `content_${i18n.defaultLocale}` as keyof Collections
  const content = await queryCollectionNavigation(collection)

  return content || (
    !content && locale.value !== i18n.defaultLocale
      ? queryCollectionNavigation(fallbackCollection)
      : content
  )
}, {
  watch: [locale],
  transform: (data) => {
    if (!data) {
      return { footer: [], legal: [] }
    }

    let nav = data.filter(nav => !nav.path.includes(LEGAL_PAGES_PATH_PREFIX))

    let usefulLinksNavigation = nav.find(nav => nav.path.endsWith('/useful-links'))

    if (usefulLinksNavigation) {
      usefulLinksNavigation = { ...usefulLinksNavigation }
      const children = usefulLinksNavigation.children ?? []

      if (!children.some(child => child.path === '/account'))
        children.unshift({ title: t('page.account.title'), path: '/account' })

      if (!children.some(child => child.path === '/track-order'))
        children.push({ title: t('order_tracking.title'), path: '/track-order' })

      usefulLinksNavigation.children = children
      nav = nav.map(item => item.path === usefulLinksNavigation!.path ? usefulLinksNavigation! : item)
    }

    const footer = nav?.map(nav => ({
      label: nav.title,
      children: nav.children?.map(child => ({
        label: child.title,
        to: localePath(child.path),
      })),
    }))

    const legal = (data?.find(nav => nav.path.includes(LEGAL_PAGES_PATH_PREFIX))?.children ?? []).map(nav => ({
      label: nav.title,
      to: localePath(nav.path),
    }))

    return { footer, legal }
  },
})

const benefits = computed(() => [
  {
    icon: 'lucide:gift',
    text: t('policies.gift_with_every_purchase'),
  },
  {
    icon: 'lucide:truck',
    text: t('policies.free_shipping'),
  },
  {
    icon: 'lucide:shield-check',
    text: t('policies.customer_satisfaction_guarantee'),
  },
  {
    icon: 'lucide:package-2',
    text: t('product.shipping_guarantee.label'),
  },
  {
    icon: 'lucide:shield-half',
    text: t('payment_acceptance.secure_payment'),
  },
  {
    icon: 'lucide:badge-check',
    text: t('policies.shop_with_confidence'),
  },
  {
    icon: 'lucide:corner-down-right',
    text: t('policies.free_exchanges'),
  },
  {
    icon: 'lucide:headset',
    text: t('policies.satisfaction_guarantee.customer_support.title'),
  },
  {
    icon: 'lucide:leaf',
    text: t('policies.eco_friendly_packaging'),
  },
])
</script>

<template>
  <footer class="pb-safe">
    <div data-testid="footer" class="mt-12 flex flex-col">
      <!--      -->

      <nav class="m-auto max-w-6xl wfull flex flex-wrap">
        <div data-testid="contactsSection" class="grow basis-[33.333%] p-6">
          <NuxtLinkLocale to="/" data-testid="footerLogo" class="flex items-center">
            <UzoLogo class="h-8 w-auto" />
          </NuxtLinkLocale>

          <div data-testid="socialLinks" class="mt2 flex">
            <NuxtLink
              v-for="social in socials" :key="social.label"
              :href="social.href"
              data-testid="link"
              class="my4 me2 flex"
            >
              <span class="sr-only" v-text="social.label" />
              <Icon :name="social.icon" class="text-xl" />
            </NuxtLink>
          </div>
        </div>

        <div data-testid="shoppingWithUsBenefits" class="min-w-xs grow basis-[33.333%] p6">
          <h3 class="text-sm font-semibold">
            {{ t('footer.shopping_with', [config.public.storefront.name]) }}
          </h3>
          <ul class="mt-6 space-y-4">
            <li
              v-for="(benefit, bIndex) in benefits"
              :key="bIndex"
              class="relative"
            >
              <span class="group flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary focus-visible:outline-primary">
                <span class="inline-flex items-center truncate">
                  <Icon class="me-4 size-6" :name="benefit.icon" />
                  {{ benefit.text }}
                </span>
              </span>
            </li>
          </ul>
        </div>

        <div data-testid="recentCustomerReviews" class="min-w-xs grow basis-[33.333%] p6">
          <h3 class="text-sm font-semibold">
            {{ t('footer.reviews.title') }}
          </h3>
          <ul class="mt-6 space-y-4">
            <FooterCustomerReviews />
          </ul>
        </div>
      </nav>

      <UiSeparator class="my-6" />

      <nav v-if="navigation?.footer?.length" class="m-auto max-w-6xl wfull">
        <UiFooterColumns :columns="navigation.footer" class="p-6" />
      </nav>

      <UiSeparator class="my-6" />

      <div class="m-auto max-w-6xl wfull">
        <nav data-testid="paymentProviders" class="px6">
          <div class="flex flex-col gap-4 md:flex-row" aria-label="Footer">
            <div>
              <h3 class="text-sm font-semibold">
                {{ t('footer.security_certification.title') }}
              </h3>
              <div class="col-span-1 flex flex-wrap items-center justify-start gap-2">
                <Icon name="certificate:visa-secure" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="certificate:mastercard-id-check" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="certificate:amex-safekey" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="certificate:j-secure" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="certificate:discover-protectbuy" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="certificate:pci-dss-compliant" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="certificate:digicert-secured" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="certificate:trusted-site" class="mr4 mt2 text-3xl" mode="svg" />
              </div>
            </div>

            <div>
              <h3 class="text-sm font-semibold">
                {{ t('footer.accepted_payments.title') }}
              </h3>
              <div class="col-span-1 flex flex-wrap items-center justify-start gap-2">
                <Icon name="payment:visa" class="mr4 mt2 text-lg" mode="svg" />
                <Icon name="payment:mastercard" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="payment:amex" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="payment:discover-2" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="payment:dinersclub-square" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="payment:maestro" class="mr4 mt2 text-2xl" mode="svg" />
                <Icon name="payment:jcb" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="payment:cb" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="payment:apple-pay" class="mr4 mt2 text-3xl" mode="svg" />
                <Icon name="payment:google-pay" class="mr4 mt2 text-3xl" mode="svg" />
              </div>
            </div>
          </div>
        </nav>
      </div>

      <UiSeparator class="mt-6" />

      <UiFooter v-if="navigation?.legal?.length" class="bg-muted">
        <!--        -->

        <UiNavigationMenu class="m-auto max-w-6xl wfull p-6">
          <UiNavigationMenuList class="flex flex-wrap gap-3">
            <UiNavigationMenuItem v-for="item in navigation.legal" :key="item.to">
              <NuxtLink v-slot="{ isActive, href, navigate }" :to="item.to" custom>
                <UiNavigationMenuLink :active="isActive" :href class="group flex items-center gap-1.5 pb-6 text-sm focus-visible:outline-primary" :class="isActive ? 'text-primary font-medium' : 'text-muted-foreground hover:text-primary transition-colors'" @click="navigate">
                  {{ item.label }}
                </UiNavigationMenuLink>
              </NuxtLink>
            </UiNavigationMenuItem>
          </UiNavigationMenuList>
        </UiNavigationMenu>
      </UiFooter>
    </div>
  </footer>
</template>
